import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { providerApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const PaymentSetup = ({ providerData }) => {
  const { t } = useTranslation();
  const { refreshProviderData } = useAuth();
  const [status, setStatus] = useState(providerData.paypalOnboardingStatus || 'NOT_STARTED');
  const [merchantId, setMerchantId] = useState(providerData.paypalMerchantId || null);
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [disconnectLoading, setDisconnectLoading] = useState(false);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (providerData.paypalMerchantId) {
      refreshStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshStatus = async () => {
    setStatusLoading(true);
    setError('');
    try {
      const data = await providerApi.getPaypalOnboardingStatus();
      setStatus(data.status);
      setMerchantId(data.merchantId);
      if (data.status === 'COMPLETED') {
        // Propagate to AuthContext so PayoutOnboardingBanner disappears without a full reload.
        await refreshProviderData();
      }
    } catch (e) {
      setError(t('payments.statusFailed'));
    } finally {
      setStatusLoading(false);
    }
  };

  const handleConnect = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await providerApi.getPaypalOnboardingLink();
      window.open(data.onboardingUrl, '_blank');
    } catch (e) {
      setError(t('payments.linkFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnectDialogOpen(false);
    setDisconnectLoading(true);
    setError('');
    try {
      await providerApi.disconnectPaypalPayout();
      setStatus('NOT_STARTED');
      setMerchantId(null);
      await refreshProviderData();
    } catch (e) {
      setError(t('payments.disconnectFailed'));
    } finally {
      setDisconnectLoading(false);
    }
  };

  const statusChip = () => {
    switch (status) {
      case 'COMPLETED':
        return <Chip icon={<CheckCircleIcon />} label={t('payments.connected')} color="success" />;
      case 'PENDING':
        return <Chip label={t('payments.pending')} color="warning" />;
      default:
        return <Chip label={t('payments.notConnected')} color="default" />;
    }
  };

  return (
    <Card sx={{ borderRadius: 2 }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AccountBalanceWalletIcon sx={{ mr: 1, color: '#667eea' }} />
          <Typography variant="h6" fontWeight="bold">
            {t('payments.title')}
          </Typography>
          <Box sx={{ ml: 'auto' }}>{statusChip()}</Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {status !== 'COMPLETED' && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {t('payments.intro')}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {status === 'COMPLETED' ? (
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('payments.connectedBody')}
            </Typography>
            {merchantId && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                {t('payments.merchantId')}: {merchantId}
              </Typography>
            )}
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => setDisconnectDialogOpen(true)}
              disabled={disconnectLoading}
              startIcon={disconnectLoading ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {t('payments.disconnectButton')}
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={handleConnect}
              disabled={loading}
              sx={{ background: '#0070ba', '&:hover': { background: '#005ea6' } }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : t('payments.connectButton')}
            </Button>

            <Button
              variant="outlined"
              onClick={refreshStatus}
              disabled={statusLoading}
            >
              {statusLoading ? <CircularProgress size={20} /> : t('payments.refreshButton')}
            </Button>
          </Box>
        )}

        {status !== 'COMPLETED' && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            {t('payments.refreshHint')}
          </Typography>
        )}
      </CardContent>

      <Dialog open={disconnectDialogOpen} onClose={() => setDisconnectDialogOpen(false)}>
        <DialogTitle>{t('payments.disconnectConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('payments.disconnectConfirmBody')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisconnectDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleDisconnect} color="error" variant="contained">
            {t('payments.disconnectConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default PaymentSetup;
